import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Typography, useTheme } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon'
import { Children, FC, ReactElement, ReactNode, isValidElement, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useCanNavigateTo } from '../../../hooks/useCanNavigateTo'

interface NavItemProps {
  isOpen: boolean
  to?: string
  label: string
  icon?: ReactNode
  testId: string
  children?: ReactNode
}

type NavChildElement = ReactElement<{ to?: string }>

const isNavChildElement = (child: ReactNode): child is NavChildElement => {
  return isValidElement(child) && typeof child.props === 'object' && child.props !== null && 'to' in child.props
}

export const NavItem: FC<NavItemProps> = ({ isOpen, to, label, icon, testId, children }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isAuthorized = useCanNavigateTo()
  const [isSubnavOpen, setIsSubnavOpen] = useState(false)
  const hasChildren = Children.count(children) > 0

  const rowSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 'var(--spacing-s)',
    inlineSize: '100%',
    maxBlockSize: 'var(--spacing-3xl)',
    paddingBlockStart: 'var(--spacing-s)',
    paddingBlockEnd: 'var(--spacing-s)',
    paddingInline: isOpen ? 'var(--spacing-s)' : 'var(--spacing-l)',
    backgroundColor: 'transparent',
    fontSize: 'var(--size-s)',
    textDecorationLine: 'none',
    whiteSpace: 'nowrap',
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  } as const

  const labelNode = (
    <Typography
      component="span"
      sx={{
        color: 'var(--text-primary)',
        display: isOpen ? 'block' : 'none',
        opacity: isOpen ? 1 : 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
        textAlign: 'start',
        transition: `
          display ${theme.transitions.duration.leavingScreen}ms ${theme.transitions.easing.sharp} allow-discrete,
          opacity ${theme.transitions.duration.leavingScreen}ms ${theme.transitions.easing.sharp}
        `
      }}
    >
      {label}
    </Typography>
  )

  const iconNode = icon ? <ListItemIcon sx={{ minWidth: 'auto', color: 'var(--shape-default)' }}>{icon}</ListItemIcon> : null
  const parentToggleIcon = isSubnavOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />

  const firstChildPath = Children.toArray(children).find(isNavChildElement)?.props.to

  const handleParentClick = () => {
    const nextValue = !isSubnavOpen
    setIsSubnavOpen(nextValue)

    if (nextValue && typeof firstChildPath === 'string') {
      navigate(firstChildPath)
    }
  }

  if (to && !isAuthorized(to)) return null

  return (
    <Box
      sx={{
        display: hasChildren ? 'flex' : 'contents',
        flexDirection: 'column',
        '.nav-link > *': {
          color: 'inherit'
        },
        '.nav-link': {
          color: 'var(--text-primary)',
          ...rowSx
        },
        '.nav-link__active': {
          backgroundColor: 'var(--yellow-300)'
        },
        '.compass-midnight & .nav-link__active': {
          color: 'var(--text-inverted)'
        },
        '.nav-item-row': {
          ...rowSx,
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit'
        },
        '.nav-item-row__toggle-icon': {
          color: 'var(--shape-default)',
          display: 'flex',
          alignItems: 'center',
          marginInlineStart: 'auto'
        },
        '.nav-link:not(.nav-link__active):hover, .nav-item-row:hover': {
          backgroundColor: theme.palette.action.hover
        },
        '.nav-item-children': {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--bg-surface-low)'
        },
        '.nav-item-children .nav-link': {
          paddingInlineStart: 'var(--spacing-3xl)'
        }
      }}
    >
      {hasChildren ? (
        <>
          <Box
            component="button"
            type="button"
            disabled={!isOpen}
            title={isOpen ? undefined : label}
            aria-expanded={isSubnavOpen}
            aria-controls={isSubnavOpen ? `${testId}-subnav` : undefined}
            data-testid={testId}
            className="nav-item-row"
            onClick={handleParentClick}
          >
            {iconNode}
            {labelNode}
            {isOpen && (
              <Box className="nav-item-row__toggle-icon" data-testid={`${testId}-toggle-${isSubnavOpen ? 'expanded' : 'collapsed'}`}>
                {parentToggleIcon}
              </Box>
            )}
          </Box>
          {isOpen && isSubnavOpen ? (
            <Box className="nav-item-children" id={`${testId}-subnav`}>
              {children}
            </Box>
          ) : null}
        </>
      ) : (
        <NavLink
          to={to ?? '#'}
          title={isOpen ? undefined : label}
          data-testid={testId}
          // Using classes to allow styling hover states
          className={({ isActive }) => (isActive ? 'nav-link nav-link__active' : 'nav-link')}
        >
          {iconNode}
          {labelNode}
        </NavLink>
      )}
    </Box>
  )
}
